import { c as _c } from "react/compiler-runtime";
/**
 * Subscription Status Service (App-wide gating)
 *
 * Used throughout the app to check if a user has an active subscription.
 * For subscription management (upgrade, cancel, billing history), see:
 *   @/features/settings/services/subscription.service.ts
 */

import { Subscription } from "@/types/subscriptions";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { isDemoEmail } from "@/lib/demo-utils";
import { shouldUseMockData } from "@/lib/mock-data/utils";
import { apiClient } from "@/services/api-client";
export interface SubscriptionStatusRecord {
  status: "active" | "cancelled" | "expired" | "unknown";
  tier: string | null;
  source: string;
  stale: boolean;
  whop_membership_id: string | null;
  next_billing_date: number | null;
  amount: number | null;
  manage_url: string | null;
  // True when the backend could not reach an answer at all (DB error or Whop
  // unreachable) — retryable, NOT a no-entitlement verdict. Optional so a
  // frontend deploy that leads the backend keeps the previous behavior.
  lookup_degraded?: boolean;
  degraded_reason?: "db_error" | "whop_unreachable" | null;
}
export interface SubscriptionReceiptRecord {
  amount: number | null;
  currency: string | null;
  paid_at: number | null;
}

/**
 * THE client-side `/subscriptions/status` read (ARCH S2 / D5).
 *
 * Every client consumer (SubscriptionMonitor, RenewalReminderWrapper, …)
 * shares this ONE query key, so a cold dashboard load performs a single
 * status fetch instead of one per consumer. The middleware wall and the
 * dashboard-layout RSC gate are AUTHORIZATION reads and deliberately stay
 * separate — deduping those means skipping an entitlement check
 * (whop-integration skill: dedupe on the backend, never by skipping the
 * layout gate).
 */
export function useSubscriptionStatus() {
  const $ = _c(7);
  const {
    data: session
  } = useSession();
  const t0 = session?.user?.email;
  let t1;
  if ($[0] !== t0) {
    t1 = isDemoEmail(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDemo = t1;
  const t2 = session?.user?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = ["subscription-status", t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!session?.user?.id && !isDemo;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      queryKey: t3,
      enabled: t4,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 60000,
      queryFn: _temp
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return useQuery(t5);
}
async function _temp() {
  const {
    data,
    status
  } = await apiClient.getV2("/subscriptions/status");
  if (status !== 200) {
    throw new Error("Failed to fetch subscription status");
  }
  return data;
}
export function useSubscriptionReceipt() {
  const $ = _c(7);
  const {
    data: session
  } = useSession();
  const t0 = session?.user?.email;
  let t1;
  if ($[0] !== t0) {
    t1 = isDemoEmail(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDemo = t1;
  const t2 = session?.user?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = ["subscription-receipt", t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!session?.user?.id && !isDemo;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      queryKey: t3,
      enabled: t4,
      staleTime: 60000,
      queryFn: _temp2
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return useQuery(t5);
}
async function _temp2() {
  const {
    data,
    status
  } = await apiClient.getV2("/subscriptions/receipt");
  if (status !== 200) {
    throw new Error("Failed to fetch subscription receipt");
  }
  return data;
}
class SubscriptionLookupError extends Error {
  constructor(readonly status: number, readonly retryable: boolean) {
    super("Failed to fetch subscriptions");
    this.name = "SubscriptionLookupError";
  }
}

/**
 * Mock subscription data for demo mode
 * Provides a valid "Pro" subscription that won't expire for 30 days
 */
function getMockSubscription(userId: string): Subscription {
  const now = Date.now();
  const thirtyDaysFromNow = now + 30 * 24 * 60 * 60 * 1000;
  return {
    id: `mock_sub_${userId}`,
    billing_period_start: now,
    billing_period_end: thirtyDaysFromNow,
    product_id: "demo_pro_plan",
    product_title: "SigmaVue Pro (Demo)",
    user_id: userId,
    is_expired: false,
    not_found: false
  };
}
export const subscriptionsService = {
  useSubscriptions(userId?: string) {
    const {
      data: session
    } = useSession();
    const isMockMode = shouldUseMockData();
    return useQuery({
      queryKey: ["subscriptions", userId],
      queryFn: async () => {
        if (!userId) {
          throw new Error("user ID is required");
        }

        // Return mock subscription for demo mode
        if (isMockMode) {
          return getMockSubscription(userId);
        }
        const response = await fetch(`/api/subscriptions/${userId}`);
        if (response.status === 404) {
          return {
            billing_period_start: 0,
            billing_period_end: 0,
            product_id: "",
            product_title: "",
            user_id: userId,
            is_expired: false,
            not_found: true
          } as Subscription;
        }
        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          const retryable = response.status >= 500 || typeof errorBody === "object" && errorBody !== null && "retryable" in errorBody && errorBody.retryable === true;
          throw new SubscriptionLookupError(response.status, retryable);
        }
        const data = await response.json();
        // SIG-249: a cold-load/degraded response can be 200 with a body that
        // has no billing_period_end — calling .toString() on undefined crashed
        // React Query ("Cannot read properties of undefined"). Degrade to a
        // not_found subscription instead of throwing.
        if (data?.billing_period_end == null || !Number.isFinite(Number(data.billing_period_end))) {
          return {
            billing_period_start: 0,
            billing_period_end: 0,
            product_id: "",
            product_title: "",
            user_id: userId,
            is_expired: false,
            not_found: true
          } as Subscription;
        }
        const expiredDateLength = data.billing_period_end.toString().length; // Used to determine if the time is in seconds or milliseconds
        return {
          ...data,
          is_expired: data.billing_period_end * (expiredDateLength === 10 ? 1000 : 1) < Date.now() // check if the subscription is expired
        } as Subscription;
      },
      // Enable query if we have userId AND (mock mode OR access token)
      enabled: !!userId && (isMockMode || !!session?.accessToken),
      // Aggressive caching to prevent re-fetches on navigation
      staleTime: 5 * 60 * 1000,
      // 5 minutes
      gcTime: 10 * 60 * 1000,
      // Keep in cache for 10 minutes
      // A 5xx is an indeterminate entitlement lookup, not "no access". Retry
      // before exposing the error state; SubscriptionMonitor keeps the member
      // signed in while this query is degraded.
      retry: (failureCount, error) => error instanceof SubscriptionLookupError && error.retryable && failureCount < 3,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    });
  }
};